import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function CalendarPicker({ slots, onSelectDate, selectedDate }) {
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const availDates = new Set(Object.keys(slots || {}));

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const today = new Date().toISOString().split('T')[0];

  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); } else setViewMonth(m => m-1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); } else setViewMonth(m => m+1); };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={styles.cal}>
      <div style={styles.calHeader}>
        <button style={styles.calNav} onClick={prevMonth}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 16 }}>{MONTHS[viewMonth]} {viewYear}</span>
        <button style={styles.calNav} onClick={nextMonth}>›</button>
      </div>
      <div style={styles.calGrid}>
        {DAYS.map(d => <div key={d} style={styles.calDayLabel}>{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const dateStr = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          const avail = availDates.has(dateStr);
          const isPast = dateStr <= today;
          const isSelected = dateStr === selectedDate;
          return (
            <div key={dateStr} onClick={() => avail && !isPast && onSelectDate(dateStr)}
              style={{
                ...styles.calDay,
                background: isSelected ? '#EE7203' : avail && !isPast ? '#fff' : 'transparent',
                color: isSelected ? '#fff' : avail && !isPast ? '#1a202c' : '#cbd5e0',
                fontWeight: isSelected ? 800 : avail ? 700 : 400,
                cursor: avail && !isPast ? 'pointer' : 'default',
                border: isSelected ? '2px solid #EE7203' : avail && !isPast ? '2px solid #EE7203' : '2px solid transparent',
                boxShadow: isSelected ? '0 2px 8px rgba(238,114,3,0.4)' : 'none',
              }}>
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BookingPage() {
  const token = window.location.pathname.split('/').pop();
  const [appt, setAppt] = useState(null);
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Password gate
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [checkingPassword, setCheckingPassword] = useState(false);

  // Contact editing state
  const [activeContact, setActiveContact] = useState(null); // the contact being booked for
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [nameChanged, setNameChanged] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/appointments/book/${token}`),
      api.get(`/appointments/book/${token}/slots`)
    ]).then(([apptRes, slotsRes]) => {
      const a = apptRes.data;
      setAppt(a);
      setSlots(slotsRes.data.slots || {});
      if (a.status === 'confirmed') setStep('confirmed');
      else if (a.status === 'cancelled') setStep('cancelled');
      // Pre-populate from first contact
      if (a.prefill_contacts?.length) {
        const c = a.prefill_contacts[0];
        setActiveContact(c);
        const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');
        setEditedName(fullName);
        setEditedEmail(c.email || '');
        setName(fullName);
        setEmail(c.email || '');
      }
    }).catch(e => setError(e.response?.data?.error || 'Appointment link not found.'))
    .finally(() => setLoading(false));
  }, [token]);

  // Sync name/email → booking fields, track if changed
  useEffect(() => {
    setName(editedName);
    if (activeContact) {
      const orig = [activeContact.first_name, activeContact.last_name].filter(Boolean).join(' ');
      setNameChanged(editedName !== orig);
    }
  }, [editedName]);
  useEffect(() => {
    setEmail(editedEmail);
    if (activeContact) setEmailChanged(editedEmail !== (activeContact.email || ''));
  }, [editedEmail]);

  const saveContactChanges = async () => {
    if (!activeContact) return;
    setSavingContact(true);
    try {
      const [fn, ...rest] = editedName.trim().split(' ');
      await api.patch(`/appointments/book/${token}/contact/${activeContact.id}`, {
        first_name: fn || editedName, last_name: rest.join(' ') || '',
        email: editedEmail,
      });
      setActiveContact(c => ({ ...c, first_name: fn || editedName, last_name: rest.join(' ') || '', email: editedEmail }));
      setNameChanged(false); setEmailChanged(false);
    } catch(e) {}
    setSavingContact(false);
  };

  const loadVersions = async () => {
    if (!activeContact) return;
    try {
      const res = await api.get(`/appointments/book/${token}/contact/${activeContact.id}/versions`);
      setVersions(res.data || []);
    } catch(e) { setVersions([]); }
    setShowVersions(true);
  };

  const revertTo = async (v) => {
    if (!activeContact) return;
    const fullName = [v.first_name, v.last_name].filter(Boolean).join(' ');
    setEditedName(fullName);
    setEditedEmail(v.email || '');
    setShowVersions(false);
    // Immediately save the revert
    try {
      await api.patch(`/appointments/book/${token}/contact/${activeContact.id}`, {
        first_name: v.first_name, last_name: v.last_name || '', email: v.email,
      });
      setActiveContact(c => ({ ...c, first_name: v.first_name, last_name: v.last_name || '', email: v.email || '' }));
      setNameChanged(false); setEmailChanged(false);
    } catch(e) {}
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d + 'T12:00:00');
    return dt.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
  };

  const fmtTs = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  const confirmBooking = async () => {
    if (!name) return;
    // Save any unsaved contact changes first
    if ((nameChanged || emailChanged) && activeContact) await saveContactChanges();
    setSubmitting(true);
    try {
      await api.post(`/appointments/book/${token}/confirm`, {
        date: selectedDate, start_time: selectedTime,
        booker_name: name, booker_email: email || null
      });
      setAppt(a => ({ ...a, confirmed_date: selectedDate, confirmed_start_time: selectedTime, confirmed_by_name: name, status: 'confirmed' }));
      setStep('confirmed');
    } catch(e) { alert(e.response?.data?.error || 'Failed to confirm. Please try again.'); }
    setSubmitting(false);
  };

  const cancelBooking = async () => {
    setSubmitting(true);
    try {
      await api.post(`/appointments/book/${token}/cancel`, { reason: cancelReason || null });
      setStep('cancelled');
    } catch(e) { alert(e.response?.data?.error || 'Failed to cancel.'); }
    setSubmitting(false);
  };

  const reschedule = async () => {
    setSubmitting(true);
    try {
      await api.post(`/appointments/book/${token}/reschedule`);
      const slotsRes = await api.get(`/appointments/book/${token}/slots`);
      setSlots(slotsRes.data.slots || {});
      setAppt(a => ({ ...a, status: 'rescheduled' }));
      setSelectedDate(null); setSelectedTime(null);
      setStep('calendar');
    } catch(e) { alert(e.response?.data?.error || 'Failed.'); }
    setSubmitting(false);
  };

  if (loading) return (
    <div style={styles.page}><div style={styles.card}><div style={{ textAlign:'center', padding: 40, color:'#718096' }}>Loading...</div></div></div>
  );
  if (error) return (
    <div style={styles.page}><div style={styles.card}><div style={{ textAlign:'center', padding: 40 }}><div style={{ fontSize:48, marginBottom:16 }}>🔗</div><h2 style={{ color:'#1a202c' }}>Link not found</h2><p style={{ color:'#718096' }}>{error}</p></div></div></div>
  );

  // Password gate — show before anything else if required and not yet verified
  if (appt?.requires_password && !passwordVerified) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, maxWidth: 420 }}>
          <div style={styles.header}>
            {appt?.company_logo_url && <img src={`/api/files/${appt.company_logo_url}`} alt="" style={{ maxHeight:48, maxWidth:160, objectFit:'contain', marginBottom:12 }} />}
            <div style={{ fontSize:36, marginBottom:8 }}>🔒</div>
            <h2 style={{ fontSize:20, fontWeight:800, color:'#1a202c', margin:'0 0 6px' }}>
              {appt?.title || 'Appointment Request'}
            </h2>
            <p style={{ color:'#718096', fontSize:14, margin:0 }}>
              This booking form is password protected. Please enter the password to continue.
            </p>
          </div>
          <div style={{ padding:'0 8px 24px' }}>
            <input
              type="password"
              placeholder="Enter password…"
              value={passwordInput}
              onChange={e => { setPasswordInput(e.target.value); setPasswordError(''); }}
              onKeyDown={async e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!passwordInput.trim()) return;
                  setCheckingPassword(true);
                  try {
                    await api.post(`/appointments/book/${token}/verify-password`, { password: passwordInput });
                    setPasswordVerified(true);
                  } catch(err) {
                    setPasswordError(err.response?.data?.error || 'Incorrect password');
                  }
                  setCheckingPassword(false);
                }
              }}
              style={{
                width:'100%', padding:'10px 14px', fontSize:16,
                border: passwordError ? '2px solid #FC8181' : '2px solid #e2e8f0',
                borderRadius:8, outline:'none', marginBottom:8, boxSizing:'border-box',
              }}
              autoFocus
            />
            {passwordError && (
              <p style={{ color:'#E53E3E', fontSize:13, margin:'0 0 10px' }}>
                ✗ {passwordError}
              </p>
            )}
            <button
              disabled={checkingPassword || !passwordInput.trim()}
              onClick={async () => {
                if (!passwordInput.trim()) return;
                setCheckingPassword(true);
                try {
                  await api.post(`/appointments/book/${token}/verify-password`, { password: passwordInput });
                  setPasswordVerified(true);
                } catch(err) {
                  setPasswordError(err.response?.data?.error || 'Incorrect password');
                }
                setCheckingPassword(false);
              }}
              style={{
                width:'100%', padding:'11px', fontSize:15, fontWeight:700,
                background: checkingPassword ? '#CBD5E0' : '#EE7203',
                color:'white', border:'none', borderRadius:8, cursor: checkingPassword ? 'not-allowed' : 'pointer',
              }}>
              {checkingPassword ? 'Checking…' : 'Continue →'}
            </button>
            <p style={{ textAlign:'center', color:'#A0AEC0', fontSize:12, marginTop:16 }}>
              Powered by <a href="https://www.ForgeBOS.com" target="_blank" rel="noreferrer" style={{ color:'#A0AEC0' }}>ForgeBOS</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const typeLabel = appt?.appointment_type === 'remote_video' ? '📹 Remote video call'
    : appt?.appointment_type === 'in_person_visit_us' ? '🤝 We will visit you'
    : `🏢 ${appt?.premises_name || appt?.manual_location || 'Our premises'}`;

  const durText = appt?.duration_minutes >= 60
    ? `${appt.duration_minutes/60}hr${appt.duration_minutes>60?'s':''}` : `${appt?.duration_minutes}min`;

  const timeSlotsForDate = slots[selectedDate] || [];
  const hasChanges = nameChanged || emailChanged;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          {appt?.company_logo_url && <img src={`/api/files/${appt.company_logo_url}`} alt="" style={{ maxHeight:56, maxWidth:180, objectFit:'contain', marginBottom:12 }} />}
          <h1 style={{ fontSize:22, fontWeight:800, color:'#1a202c', margin:'0 0 4px' }}>
            {appt?.title || `Meeting with ${appt?.user_name}`}
          </h1>
          <p style={{ color:'#718096', margin:'0 0 8px', fontSize:14 }}>Scheduled by <strong>{appt?.user_name}</strong></p>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'center' }}>
            <span style={styles.chip}>{typeLabel}</span>
            <span style={styles.chip}>⏱ {durText}</span>
          </div>
          {appt?.message && <div style={styles.msgBox}>{appt.message}</div>}
        </div>

        <div style={styles.body}>

          {/* CONFIRMED */}
          {step === 'confirmed' && (
            <div style={{ textAlign:'center', padding:'24px 0' }}>
              <div style={{ fontSize:56, marginBottom:12 }}>✅</div>
              <h2 style={{ color:'#276749', margin:'0 0 8px' }}>Appointment Confirmed</h2>
              {appt?.confirmed_date && (
                <div style={styles.confirmedBox}>
                  <div style={{ fontWeight:700, fontSize:18, color:'#1a202c' }}>{fmtDate(appt.confirmed_date)}</div>
                  <div style={{ color:'#EE7203', fontWeight:700, fontSize:16, marginTop:4 }}>
                    {appt.confirmed_start_time?.slice(0,5)} – {appt.confirmed_end_time?.slice(0,5) || ''}
                  </div>
                  {appt.confirmed_by_name && <div style={{ color:'#718096', marginTop:4, fontSize:14 }}>Booked by {appt.confirmed_by_name}</div>}
                </div>
              )}
              <p style={{ color:'#718096', fontSize:14, marginTop:16 }}>A calendar invitation has been sent to your email address.</p>
              <div style={{ display:'flex', gap:12, justifyContent:'center', marginTop:24, flexWrap:'wrap' }}>
                <button style={styles.btnSecondary} onClick={() => setStep('cancel_form')}>Cancel Appointment</button>
                <button style={styles.btnSecondary} onClick={reschedule} disabled={submitting}>Reschedule</button>
              </div>
            </div>
          )}

          {/* CANCELLED */}
          {step === 'cancelled' && (
            <div style={{ textAlign:'center', padding:'24px 0' }}>
              <div style={{ fontSize:56, marginBottom:12 }}>❌</div>
              <h2 style={{ color:'#c53030' }}>Appointment Cancelled</h2>
              {appt?.cancellation_reason && <p style={{ color:'#718096' }}>Reason: {appt.cancellation_reason}</p>}
              <p style={{ color:'#a0aec0', fontSize:14 }}>{appt?.user_name} has been notified.</p>
            </div>
          )}

          {/* CANCEL FORM */}
          {step === 'cancel_form' && (
            <div>
              <h3 style={{ margin:'0 0 16px', color:'#1a202c' }}>Cancel Appointment</h3>
              <label style={styles.label}>Reason for cancellation (optional)</label>
              <textarea style={styles.textarea} rows={4} value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="Please let us know why you are cancelling..." />
              <div style={{ display:'flex', gap:12, marginTop:16 }}>
                <button style={styles.btnGhost} onClick={() => setStep('confirmed')}>Back</button>
                <button style={{ ...styles.btnDanger }} onClick={cancelBooking} disabled={submitting}>
                  {submitting ? 'Cancelling...' : 'Confirm Cancellation'}
                </button>
              </div>
            </div>
          )}

          {/* CALENDAR */}
          {step === 'calendar' && (
            <div>
              {appt?.status === 'rescheduled' && (
                <div style={styles.rescheduledBanner}>
                  🔄 You are rescheduling your appointment. Please select a new date.
                </div>
              )}
              {Object.keys(slots).length === 0 ? (
                <div style={{ textAlign:'center', padding:32, color:'#718096' }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>📅</div>
                  <p>No available dates found. Please contact {appt?.user_name} directly.</p>
                </div>
              ) : (
                <>
                  <p style={{ color:'#718096', marginBottom:16, fontSize:14 }}>
                    Please select a date from the calendar below. Highlighted dates have availability.
                  </p>
                  <CalendarPicker slots={slots} selectedDate={selectedDate}
                    onSelectDate={d => { setSelectedDate(d); setSelectedTime(null); setStep('time'); }} />
                </>
              )}
            </div>
          )}

          {/* TIME SLOTS */}
          {step === 'time' && selectedDate && (
            <div>
              <button style={styles.backBtn} onClick={() => { setStep('calendar'); setSelectedDate(null); }}>← Back to calendar</button>
              <h3 style={{ margin:'12px 0 16px', color:'#1a202c' }}>Available times on {fmtDate(selectedDate)}</h3>
              {timeSlotsForDate.length === 0 ? (
                <p style={{ color:'#718096' }}>No slots available on this date.</p>
              ) : (
                <div style={styles.timeGrid}>
                  {timeSlotsForDate.map(s => (
                    <button key={s.start_time} style={{ ...styles.timeSlot, ...(selectedTime === s.start_time ? styles.timeSlotSelected : {}) }}
                      onClick={() => { setSelectedTime(s.start_time); setStep('details'); }}>
                      {s.start_time.slice(0,5)} – {s.end_time.slice(0,5)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* DETAILS */}
          {step === 'details' && (
            <div>
              <button style={styles.backBtn} onClick={() => setStep('time')}>← Back to times</button>
              <h3 style={{ margin:'12px 0 8px', color:'#1a202c' }}>Your details</h3>
              <div style={styles.selectedSlotBanner}>
                📅 {fmtDate(selectedDate)} at {selectedTime?.slice(0,5)}
              </div>

              <label style={styles.label}>
                Your name <span style={{ color:'#e53e3e' }}>*</span>
              </label>
              <input style={{ ...styles.input, borderColor: nameChanged ? '#EE7203' : '#e2e8f0' }}
                value={editedName} onChange={e => setEditedName(e.target.value)} placeholder="Full name" autoFocus />

              <label style={styles.label}>Email address (optional — for calendar invite)</label>
              <input style={{ ...styles.input, borderColor: emailChanged ? '#EE7203' : '#e2e8f0' }}
                type="email" value={editedEmail} onChange={e => setEditedEmail(e.target.value)} placeholder="you@example.com" />

              {/* Save / revert controls */}
              {(hasChanges || activeContact) && (
                <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:10, flexWrap:'wrap' }}>
                  {hasChanges && (
                    <button style={styles.btnSaveContact} onClick={saveContactChanges} disabled={savingContact}>
                      {savingContact ? 'Saving...' : '💾 Save contact changes'}
                    </button>
                  )}
                  {activeContact && (
                    <button style={styles.btnGhost} onClick={loadVersions}>
                      🕐 View history
                    </button>
                  )}
                  {hasChanges && activeContact && (
                    <button style={{ ...styles.btnGhost, fontSize:12 }} onClick={() => {
                      const orig = [activeContact.first_name, activeContact.last_name].filter(Boolean).join(' ');
                      setEditedName(orig); setEditedEmail(activeContact.email || '');
                    }}>
                      ↩ Revert
                    </button>
                  )}
                </div>
              )}

              {/* Version history panel */}
              {showVersions && (
                <div style={styles.versionsPanel}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                    <span style={{ fontWeight:700, fontSize:13, color:'#2d3748' }}>Contact version history</span>
                    <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, color:'#718096' }} onClick={() => setShowVersions(false)}>✕</button>
                  </div>
                  {versions.length === 0 ? (
                    <p style={{ fontSize:12, color:'#a0aec0', margin:0 }}>No previous versions recorded.</p>
                  ) : versions.map((v, i) => (
                    <div key={i} style={styles.versionRow}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'#2d3748' }}>{[v.first_name, v.last_name].filter(Boolean).join(' ')}</div>
                        <div style={{ fontSize:11, color:'#718096' }}>{v.email || '(no email)'} · {fmtTs(v.changed_at)}</div>
                      </div>
                      <button style={styles.btnRevert} onClick={() => revertTo(v)}>Revert</button>
                    </div>
                  ))}
                </div>
              )}

              <button style={{ ...styles.btnPrimary, opacity: !editedName ? 0.5 : 1 }}
                disabled={!editedName} onClick={() => setStep('confirm')}>
                Review Booking →
              </button>
            </div>
          )}

          {/* CONFIRM */}
          {step === 'confirm' && (
            <div>
              <button style={styles.backBtn} onClick={() => setStep('details')}>← Back</button>
              <h3 style={{ margin:'12px 0 16px', color:'#1a202c' }}>Confirm your booking</h3>
              <div style={styles.summaryCard}>
                <SummaryRow icon="📅" label="Date" value={fmtDate(selectedDate)} />
                <SummaryRow icon="⏰" label="Time" value={`${selectedTime?.slice(0,5)} – (${durText})`} />
                <SummaryRow icon="👤" label="With" value={appt?.user_name} />
                <SummaryRow icon="📋" label="Type" value={typeLabel} />
                <SummaryRow icon="🙋" label="Your name" value={name} />
                {email && <SummaryRow icon="✉️" label="Email" value={email} />}
              </div>
              <button style={styles.btnPrimary} onClick={confirmBooking} disabled={submitting}>
                {submitting ? 'Confirming...' : '✓ Confirm Booking'}
              </button>
              <p style={{ fontSize:12, color:'#a0aec0', marginTop:8 }}>
                {email ? 'A calendar invitation will be sent to your email.' : 'Add your email address to receive a calendar invitation.'}
              </p>
            </div>
          )}

        </div>

        <div style={styles.footer}>
          Powered by{' '}
          <a href="https://www.ForgeBOS.com" target="_blank" rel="noopener noreferrer"
            style={{ color:'#718096', fontWeight:700, textDecoration:'none' }}
            onMouseOver={e => e.target.style.textDecoration='underline'}
            onMouseOut={e => e.target.style.textDecoration='none'}>
            ForgeBOS
          </a>
          {' '}· Appointment Scheduling
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div style={{ display:'flex', gap:12, padding:'10px 0', borderBottom:'1px solid #e2e8f0' }}>
      <span style={{ width:24, textAlign:'center' }}>{icon}</span>
      <span style={{ width:60, color:'#718096', fontSize:13, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</span>
      <span style={{ color:'#1a202c', fontSize:14, fontWeight:500 }}>{value}</span>
    </div>
  );
}

const styles = {
  page: { minHeight:'100vh', background:'linear-gradient(135deg,#f7fafc 0%,#edf2f7 100%)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'32px 16px', fontFamily:'Arial,sans-serif' },
  card: { background:'#fff', borderRadius:20, boxShadow:'0 10px 40px rgba(0,0,0,0.12)', width:'100%', maxWidth:560, overflow:'hidden' },
  header: { background:'linear-gradient(135deg,#1a202c,#2d3748)', padding:'32px 32px 24px', textAlign:'center', color:'#fff' },
  body: { padding:32 },
  footer: { padding:'16px 32px', textAlign:'center', fontSize:11, color:'#a0aec0', borderTop:'1px solid #e2e8f0' },
  chip: { background:'rgba(255,255,255,0.15)', borderRadius:20, padding:'4px 12px', fontSize:13, color:'#fff' },
  msgBox: { background:'rgba(255,255,255,0.1)', borderRadius:10, padding:'12px 16px', marginTop:16, fontSize:14, color:'rgba(255,255,255,0.9)', textAlign:'left', lineHeight:'1.6' },
  cal: { border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden' },
  calHeader: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'#f7fafc', borderBottom:'1px solid #e2e8f0' },
  calNav: { background:'none', border:'1px solid #e2e8f0', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:18, color:'#4a5568' },
  calGrid: { display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, padding:12 },
  calDayLabel: { textAlign:'center', fontSize:11, fontWeight:700, color:'#a0aec0', textTransform:'uppercase', padding:'4px 0' },
  calDay: { textAlign:'center', padding:'10px 4px', borderRadius:8, fontSize:14, transition:'all 0.1s' },
  timeGrid: { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 },
  timeSlot: { padding:'12px 8px', border:'2px solid #e2e8f0', borderRadius:10, background:'#fff', cursor:'pointer', fontSize:14, fontWeight:600, color:'#4a5568', transition:'all 0.15s' },
  timeSlotSelected: { background:'#EE7203', borderColor:'#EE7203', color:'#fff' },
  label: { display:'block', fontSize:12, fontWeight:700, color:'#4a5568', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6, marginTop:16 },
  input: { width:'100%', padding:'12px 14px', fontSize:16, border:'2px solid #e2e8f0', borderRadius:10, outline:'none', boxSizing:'border-box', transition:'border-color 0.15s' },
  textarea: { width:'100%', padding:'12px 14px', fontSize:15, border:'2px solid #e2e8f0', borderRadius:10, outline:'none', resize:'vertical', fontFamily:'Arial', boxSizing:'border-box' },
  btnPrimary: { display:'block', width:'100%', padding:'16px', background:'#EE7203', color:'#fff', border:'none', borderRadius:12, fontSize:16, fontWeight:800, cursor:'pointer', marginTop:20 },
  btnSecondary: { padding:'10px 20px', background:'transparent', color:'#4a5568', border:'2px solid #e2e8f0', borderRadius:10, fontSize:14, cursor:'pointer', fontWeight:600 },
  btnGhost: { padding:'8px 14px', background:'#f7fafc', color:'#4a5568', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12, cursor:'pointer' },
  btnDanger: { padding:'10px 20px', background:'#e53e3e', color:'#fff', border:'none', borderRadius:10, fontSize:14, cursor:'pointer', fontWeight:700 },
  btnSaveContact: { padding:'8px 14px', background:'#EE7203', color:'#fff', border:'none', borderRadius:8, fontSize:12, cursor:'pointer', fontWeight:700 },
  btnRevert: { padding:'4px 10px', background:'#fff', color:'#4a5568', border:'1px solid #e2e8f0', borderRadius:6, fontSize:11, cursor:'pointer' },
  backBtn: { background:'none', border:'none', color:'#718096', fontSize:14, cursor:'pointer', padding:'0 0 4px', display:'flex', alignItems:'center', gap:4 },
  confirmedBox: { background:'#f0fff4', border:'2px solid #9ae6b4', borderRadius:12, padding:'20px 24px', margin:'16px auto', maxWidth:320, textAlign:'center' },
  summaryCard: { background:'#f7fafc', borderRadius:12, padding:'8px 16px', marginBottom:16 },
  selectedSlotBanner: { background:'#fff7ed', border:'2px solid #fed7aa', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:14, fontWeight:600, color:'#c05621' },
  rescheduledBanner: { background:'#ebf8ff', border:'2px solid #bee3f8', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:14, color:'#2b6cb0' },
  versionsPanel: { background:'#f7fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:'14px 16px', marginTop:12, marginBottom:8 },
  versionRow: { display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderBottom:'1px solid #e2e8f0' },
};
